/**
 * Reports Core Web Vitals (CLS, FID/INP, LCP, FCP, TTFB) to Sentry as
 * measurements, so production performance regressions show up alongside
 * error rates. No-op when Sentry isn't configured.
 */
import type { Metric } from "web-vitals";

type SentryLike = {
  setMeasurement?: (name: string, value: number, unit: string) => void;
  captureMessage?: (msg: string, ctx?: Record<string, unknown>) => void;
};

function getSentry(): SentryLike | null {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const w = window as any;
  return w.Sentry ?? null;
}

function report(metric: Metric) {
  const sentry = getSentry();
  // Always emit to console.debug so devs can see it without Sentry.

  console.debug("[web-vitals]", metric.name, metric.value, metric.rating);

  if (!sentry) return;
  const unit = metric.name === "CLS" ? "" : "millisecond";
  sentry.setMeasurement?.(
    `webvital.${metric.name.toLowerCase()}`,
    metric.value,
    unit,
  );

  // Surface poor scores explicitly so they trigger alerts.
  if (metric.rating === "poor") {
    sentry.captureMessage?.(`Poor Web Vital: ${metric.name}`, {
      level: "warning",
      tags: { metric: metric.name, rating: metric.rating },
      extra: { value: metric.value, id: metric.id },
    });
  }
}

export async function initWebVitals(): Promise<void> {
  try {
    const { onCLS, onINP, onLCP, onFCP, onTTFB } = await import("web-vitals");
    onCLS(report);
    onINP(report);
    onLCP(report);
    onFCP(report);
    onTTFB(report);
  } catch {
    /* noop */
  }
}
